import { Injectable, InjectionToken, inject } from '@angular/core';
import { WINDOW_TOKEN } from './window.token';
import { PreBootSessionService } from './pre-boot-session.service';
import type { HttpHeaders } from '@angular/common/http';

export const SESSION_CONFIGURATION = new InjectionToken<SessionConfiguration>('sessionConfiguration');

export interface SessionConfiguration {
  readonly loginUrl: string;
  readonly logoutUrl: string;
  readonly userId?: string;
  readonly authorizationToken?: string;
}

/**
 * Responsible for user session management, including session timeouts and logout.
 */
@Injectable({
  providedIn: 'root'
})
export class SessionService extends PreBootSessionService {
  private configuration = inject<SessionConfiguration>(SESSION_CONFIGURATION, { optional: true }) ?? { userId: 'anonymous', loginUrl: '', logoutUrl: '' };

  private storage?: Storage;

  constructor() {
    const browserWindow = inject<Window>(WINDOW_TOKEN);

    super();
    this.storage = browserWindow?.sessionStorage;
  }

  /**
   *
   * Check if a user session has timed out using UIP redirect header "Ajax-Response"
   *
   * @param responseHeaders - headers will be checked for redirection
   * @param responseBody
   * @returns true if the user session has timed out, false otherwise.
   */
  hasSessionTimedOut(responseHeaders: HttpHeaders,
                     responseBody?: { envelope: { authorizationToken: string } } | undefined): boolean {
    return this.isAjaxRedirect(responseHeaders) || this.isAuthorizationTokenInvalid(responseBody);
  }

  /**
   *
   * Terminate the user session and logout
   */
  logout(): void {
    if (!this.redirectToLoginScreenInProgress && this.configuration.logoutUrl !== '') {
      this.locationService.getCurrentLocation().replace(this.configuration.logoutUrl);
      this.redirectToLoginScreenInProgress = true;
    }
  }

  getUserId(): string {
    return this.configuration.userId ?? 'anonymous';
  }

  getAuthorizationToken(): string {
    return this.configuration.authorizationToken ?? '';
  }

  updateConfiguration(configuration: SessionConfiguration): void {
    this.configuration = configuration;
  }

  redirectToLoginPage(headers: HttpHeaders): void {
    if (!this.redirectToLoginScreenInProgress) {
      this.storage?.clear();
      this.locationService.getCurrentLocation().replace(this.getRedirectUrl(headers));
      this.redirectToLoginScreenInProgress = true;
    }
  }

  protected getLoginUrl(redirectLocation?: string): string {
    let result = '/';
    if (redirectLocation !== undefined) {
      result = redirectLocation;
    } else if (this.configuration.loginUrl !== undefined && this.configuration.loginUrl !== '') {
      result = this.configuration.loginUrl;
    }
    return result;
  }

  private isAuthorizationTokenInvalid(responseBody?: { envelope: { authorizationToken: string } }): boolean {
    if (responseBody !== undefined && responseBody !== null &&
      responseBody.envelope !== undefined &&
      responseBody.envelope.authorizationToken !== undefined) {
      return responseBody.envelope.authorizationToken !== this.configuration.authorizationToken;
    }
    return false;
  }
}
